import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import {
    getOrganizationClientRecordBillingDetailsCacheKey,
    longTermCacheStaleTime,
    onClientRecordBillingDetailsUpdate,
} from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';
import useNotify from '../useNotify';

const intl = getIntl('useOrganizationClientRecordBillingDetails', {
    errorLoadingBillingDetails: 'Error loading billing details',
    errorUpdatingBillingDetails: 'Error updating billing details',
    successUpdatingBillingDetails: 'Billing details updated successfully',
});

export type UseOrganizationClientRecordBillingDetailsArgs = {
    organizationClientRecordId: string;
    onUpdatedBillingDetails?: () => void;
};

export type UseOrganizationClientRecordBillingDetailsReturnValue = {
    isLoadingBillingDetails: boolean;
    isFetchingBillingDetails: boolean;
    billingDetails: APITypes.organizationService.GetOrganizationClientRecordBillingDetailsResponse | null;
    updateBillingDetails: (
        billingDetails: APITypes.organizationService.UpdateOrganizationClientRecordBillingDetailsRequest,
    ) => void;
    isUpdatingBillingDetails: boolean;
};

export default function UseOrganizationClientRecordBillingDetails({
    organizationClientRecordId,
    onUpdatedBillingDetails,
}: UseOrganizationClientRecordBillingDetailsArgs): UseOrganizationClientRecordBillingDetailsReturnValue {
    const notify = useNotify();
    const organizationId = useOrganizationId();
    const billingDetailsQuery =
        useQuery<APITypes.organizationService.GetOrganizationClientRecordBillingDetailsResponse | null>({
            queryKey: getOrganizationClientRecordBillingDetailsCacheKey(organizationClientRecordId),
            queryFn: async () => {
                return await appAPI.get(
                    urls.api.organizations.clientRecords.billingDetails(organizationId, organizationClientRecordId),
                );
            },
            staleTime: longTermCacheStaleTime,
        });
    useQueryErrorNotify(billingDetailsQuery, intl('errorLoadingBillingDetails'));

    const updateBillingDetailsMutation = useMutation({
        mutationFn: async (
            billingDetails: APITypes.organizationService.UpdateOrganizationClientRecordBillingDetailsRequest,
        ) => {
            return appAPI.put(
                urls.api.organizations.clientRecords.billingDetails(organizationId, organizationClientRecordId),
                { body: billingDetails },
            );
        },
        onSuccess: () => {
            onUpdatedBillingDetails?.();
            notify('success', intl('successUpdatingBillingDetails'));
            onClientRecordBillingDetailsUpdate.resetCache({ organizationClientRecordId })();
        },
        onError: () => {
            notify('error', intl('errorUpdatingBillingDetails'));
        },
    });

    return {
        isLoadingBillingDetails: billingDetailsQuery.isLoading,
        isFetchingBillingDetails: billingDetailsQuery.isFetching,
        billingDetails: billingDetailsQuery.data || null,
        updateBillingDetails: updateBillingDetailsMutation.mutate,
        isUpdatingBillingDetails: updateBillingDetailsMutation.isPending,
    };
}
